import { cx } from './cx';

/**
 * Single-path stroke icons, drawn on a 24x24 grid.
 *
 * This set started life inside `AppShell`, where only the sidebar could reach
 * it — which is exactly why nine icons existed in the whole application and
 * every stat tile, empty state and job card was text on white. It lives here
 * now so any surface can use it.
 *
 * One `<path>` per icon rather than an icon package: the candidate site is
 * opened on low-end handsets over 4G, and the whole set below costs less than
 * the import statement for a tree-shaken icon library. Multiple sub-paths are
 * fine — `d` takes as many `M` commands as an icon needs.
 */
const STROKE: Record<string, string> = {
  // --- Navigation (moved from AppShell) ------------------------------------
  home: 'M3 10.5 12 3l9 7.5M5.25 9.75V20a1 1 0 0 0 1 1h3.5v-5.5h4.5V21h3.5a1 1 0 0 0 1-1V9.75',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM20 20l-4.2-4.2',
  clipboard:
    'M9 4h6v2H9V4Zm-2 2H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1M9 12h6M9 16h4',
  bookmark: 'M7 4h10a1 1 0 0 1 1 1v15l-6-4-6 4V5a1 1 0 0 1 1-1Z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM5 20a7 7 0 0 1 14 0',
  file: 'M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Zm0 0v4h4M9 13h6M9 17h4',
  briefcase:
    'M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 7h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Zm0 5h16',
  users: 'M9 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3 20a6 6 0 0 1 12 0M16.5 12.5a5.5 5.5 0 0 1 4.5 7.5',
  calendar:
    'M7 3v3m10-3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
  factory: 'M3 21h18M4 21V10l5 3V10l5 3V6l5 3v12M8 17h1m4 0h1m4 0h1',
  building: 'M5 21V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v17M15 9h3a1 1 0 0 1 1 1v11M8 7h4M8 11h4M8 15h4M3 21h18',
  chart: 'M4 20V10m5 10V4m5 16v-7m5 7V8M3 21h18',
  layers: 'm12 3 9 5-9 5-9-5 9-5Zm9 10-9 5-9-5m18 4-9 5-9-5',
  shield: 'M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Zm-2.5 9 2 2 3.5-4',
  history: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3.5 2',
  invoice:
    'M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Zm0 0v4h4M9.5 11h5m-5 0a2 2 0 1 1 0 4h-.5l3.5 3m-3-7h5',
  wallet:
    'M3 7a1 1 0 0 1 1-1h13a1 1 0 0 1 1 1v2M3 7v10a1 1 0 0 0 1 1h15a1 1 0 0 0 1-1v-3M3 7h16M20 10h-4a2 2 0 0 0 0 4h4a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1Z',

  // --- Job and candidate facts ---------------------------------------------
  /** Indian rupee, for salary. A currency glyph in text would not align with siblings. */
  rupee: 'M6.5 4h11M6.5 8.5h11M12.5 4c2.6 0 4.2 1.8 4.2 4.3 0 2.6-2.1 4.4-5.2 4.4H6.5l8.5 7.8',
  mapPin: 'M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Zm0-8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13.5V12l3.5 2',
  graduation: 'm12 4 10 5-10 5-10-5 10-5ZM6 11.5V17c0 1.7 2.7 3 6 3s6-1.3 6-3v-5.5',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-14v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4m0-12.8L17 7M7 17l-1.4 1.4',
  moon: 'M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z',
  target:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-4.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0-3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Zm9.5 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',

  // --- Controls -------------------------------------------------------------
  filter: 'M4 5h16l-6.5 7.5V20l-3-2v-7.5L4 5Z',
  sort: 'M8 4v16m0 0-3-3m3 3 3-3M16 20V4m0 0-3 3m3-3 3 3',
  check: 'm5 13 4.5 4.5L19 7',
  close: 'M6 6l12 12M18 6 6 18',
  plus: 'M12 5v14M5 12h14',
  menu: 'M4 7h16M4 12h16M4 17h16',
  chevronDown: 'm6 9 6 6 6-6',
  chevronRight: 'm9 6 6 6-6 6',
  chevronLeft: 'm15 6-6 6 6 6',
  arrowUp: 'M12 20V4m0 0-6 6m6-6 6 6',
  arrowDown: 'M12 4v16m0 0 6-6m-6 6-6-6',
  arrowRight: 'M4 12h16m0 0-6-6m6 6-6 6',
  external: 'M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  edit: 'M5 19h3l9.5-9.5a2.1 2.1 0 0 0-3-3L5 16v3Zm9-12 3 3',
  trash: 'M5 7h14M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6m4-6v6',
  logout: 'M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M9 8l-4 4 4 4M5 12h9',
  upload: 'M12 16V4m0 0-4 4m4-4 4 4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  download: 'M12 4v12m0 0-4-4m4 4 4-4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  share:
    'M18 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM6 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM18 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM8.2 13.2l7.6 4M15.8 6.8l-7.6 4',

  // --- Feedback and status --------------------------------------------------
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-2 8-2 8h16s-2-1-2-8M13.7 20a2 2 0 0 1-3.4 0',
  alert: 'M12 4 2.5 20h19L12 4Zm0 6v5m0 3h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13h.01M11 12h1v5h1',
  checkCircle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-3.5-9.2 2.5 2.5 5-5.3',
  trendUp: 'M3 17l6-6 4 4 8-8m0 0h-5m5 0v5',
  trendDown: 'M3 7l6 6 4-4 8 8m0 0h-5m5 0v-5',
  mail: 'M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm0 1 8 6 8-6',
  phone:
    'M6.5 3h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5L16 12l4 1.5v3a1.5 1.5 0 0 1-1.7 1.5A15.5 15.5 0 0 1 5 5.7 1.5 1.5 0 0 1 6.5 3Z',
  message: 'M12 20.5c4.7 0 8.5-3.4 8.5-7.7S16.7 5 12 5s-8.5 3.5-8.5 7.8c0 2 .8 3.9 2.2 5.3L4.5 21l4-1.1c1.1.4 2.3.6 3.5.6Z',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.8-1.7 2 1.5-2 3.5-2.3-1a8 8 0 0 1-3 1.7L14 22h-4l-.5-2.6a8 8 0 0 1-3-1.7l-2.3 1-2-3.5 2-1.5a8.3 8.3 0 0 1 0-2.6l-2-1.5 2-3.5 2.3 1a8 8 0 0 1 3-1.7L10 2h4l.5 2.6a8 8 0 0 1 3 1.7l2.3-1 2 3.5-2 1.5a8.3 8.3 0 0 1 0 2.6Z',
};

/**
 * Icons that read as a solid shape rather than an outline — a saved bookmark,
 * a filled star, a brand mark. They paint with `fill` and no stroke.
 */
const FILLED: Record<string, string> = {
  bookmarkFilled: 'M7 3.5h10a1.5 1.5 0 0 1 1.5 1.5v15.6a.6.6 0 0 1-.94.49L12 17.2l-5.56 3.89a.6.6 0 0 1-.94-.49V5A1.5 1.5 0 0 1 7 3.5Z',
  starFilled: 'm12 3.2 2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8-5.4 2.8 1-6L3.2 9.6l6.1-.9L12 3.2Z',
  /**
   * WhatsApp. Kept as a real brand glyph because "share on WhatsApp" is the
   * single most-used action for this audience, and a generic share arrow does
   * not get recognised as it.
   */
  whatsapp:
    'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.17c-.25.69-1.44 1.32-1.99 1.4-.53.08-1.2.11-1.94-.12-.45-.14-1.02-.33-1.76-.65-3.1-1.34-5.12-4.46-5.28-4.67-.15-.21-1.26-1.67-1.26-3.19 0-1.52.8-2.27 1.08-2.58.28-.31.61-.39.82-.39h.59c.19.01.44-.07.69.53.25.6.86 2.08.93 2.23.08.15.13.33.02.53-.1.21-.15.33-.3.51l-.45.54c-.15.15-.31.31-.13.61.18.3.79 1.3 1.69 2.11 1.16 1.03 2.14 1.35 2.44 1.5.3.15.48.13.66-.08.18-.21.76-.89.96-1.19.2-.3.4-.25.68-.15.28.1 1.75.83 2.05.98.3.15.5.22.57.35.07.13.07.75-.18 1.44Z',
};

export type IconName = keyof typeof STROKE | keyof typeof FILLED;

const SIZES = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-[18px] w-[18px]',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
} as const;

/**
 * Always `aria-hidden`. An icon either sits beside a label that already names
 * the thing, or it sits inside an icon-only control — and in that case the
 * control carries the accessible name, not the glyph. Labelling both makes a
 * screen reader say it twice.
 */
export function Icon({
  name,
  size = 'md',
  className,
}: {
  name: IconName;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const filled = FILLED[name as string];

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={cx('shrink-0', SIZES[size], className)}
      {...(filled
        ? { fill: 'currentColor' }
        : {
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.6,
            strokeLinecap: 'round' as const,
            strokeLinejoin: 'round' as const,
          })}
    >
      <path d={filled ?? STROKE[name as string] ?? STROKE.home!} />
    </svg>
  );
}

/** Every name the set answers to — used by the test that guards against typos. */
export const ICON_NAMES = [
  ...Object.keys(STROKE),
  ...Object.keys(FILLED),
] as readonly IconName[];
